import {
  getRoutingReasonLine,
  messageNotificationPatientId
} from "features/inbox/text-routing/routing-reason.util";
import {
  MessageNotification,
  NotificationTypes,
  isSmsNotDeliveredExtra
} from "services/types/notifications";

/** Texts not delivered: what the alert for a failed staff-typed text says. */
export type SmsNotDeliveredAlert = {
  patientName: string;
  reasonLabel: string;
  /** `Text to <patient> not delivered · <reason>` — the in-app toast. */
  toastMessage: string;
};

export type TextAlertContent = {
  notificationId: number;
  patientId: number | null;
  title: string;
  /** Message preview, then the routing "why" line when there is one. */
  body: string;
  reasonLine: string | null;
  /** Set only for a `kind: "sms_not_delivered"` notification; absent otherwise. */
  notDelivered?: SmsNotDeliveredAlert;
};

const NOT_DELIVERED_TITLE = /text to (.+?) was not delivered/i;

/**
 * The patient a "not delivered" notification names: `extra.patient_name` (the
 * backend sends it; `extra.patient` is the patient's id), else the name inside
 * the title ("Your text to Margaret Ellis was not delivered"), else "patient".
 */
export function smsNotDeliveredPatientName(
  title: string | undefined,
  extra: { patient_name?: unknown } | null | undefined
): string {
  const fromExtra = extra?.patient_name;
  if (typeof fromExtra === "string" && fromExtra.trim()) {
    return fromExtra.trim();
  }
  const fromTitle = (title ?? "").match(NOT_DELIVERED_TITLE)?.[1]?.trim();
  return fromTitle || "patient";
}

/**
 * Turns a `new.user.notification` socket payload into a patient-text alert, or
 * null when it is not one (another notification type, or a malformed payload).
 * The socket's `message` IS the notification row (same shape the bell list
 * reads — see use-notifications.tsx), so no refetch is needed to build it.
 */
export function buildTextAlertContent(
  payload: unknown
): TextAlertContent | null {
  const notification = payload as Partial<MessageNotification> | null;
  if (
    !notification ||
    notification.type !== NotificationTypes.MESSAGE ||
    typeof notification.id !== "number"
  ) {
    return null;
  }
  const extra = notification.extra;
  const patientId = messageNotificationPatientId(extra);
  const reasonLine = getRoutingReasonLine(extra);
  const description = (notification.description ?? "").trim();
  if (isSmsNotDeliveredExtra(extra)) {
    const patientName = smsNotDeliveredPatientName(notification.title, extra);
    const reasonLabel = (extra.reason_label ?? "").trim();
    return {
      notificationId: notification.id,
      patientId,
      title:
        (notification.title ?? "").trim() ||
        `Text to ${patientName} was not delivered`,
      body: [reasonLabel, description].filter(Boolean).join("\n"),
      reasonLine: null,
      notDelivered: {
        patientName,
        reasonLabel,
        toastMessage: [`Text to ${patientName} not delivered`, reasonLabel]
          .filter(Boolean)
          .join(" · ")
      }
    };
  }
  return {
    notificationId: notification.id,
    patientId,
    title: (notification.title ?? "").trim() || "New patient text",
    body: [description, reasonLine].filter(Boolean).join("\n"),
    reasonLine
  };
}
